import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { compensationGateway } from '../../compensation';
import { compensationPayTypeLabels, type WorkRateAssignmentResponse } from '../../compensation/types';
import { payConfigGateway } from '../../pay-config';
import { workRateAmountBasisLabels, type WorkRateResponse } from '../../pay-config/types';
import {
  BASE_PAY_BASES, REIMBURSEMENT_SEPARATE_SHORT, payBasisLabels, rateHelp as rateBasisHelp, rateLabels,
} from '../../healthcare-rates/types';
import { payBasisFromPayType, payTypeFromPayBasis } from '../../healthcare-rates/wireMapping';
import { useCompanyIndustry } from '../../healthcare-rates/useCompanyIndustry';

// EPN-185. Payroll Details > pay rates for ONE employee. Real data only: per-employee rates are
// EmployeeWorkRateAssignment rows owned by EPN.Compensation.Api; reusable rate templates are WorkRate rows
// owned by EPN.PayrollConfig.Api. Rates are effective-dated and there is no edit endpoint, so a change is
// "end this rate, add a new one". The server decides authorization and tenant scope; this panel only
// presents the refusals. After every successful change `onChanged` fires so the profile re-reads readiness
// from the server (the Setup Summary is never inferred here).
//
// UX / DESIGN GAP: no approved Figma frame exists for this screen. It reuses the Work Details pattern
// (pick from a list, or "+ Add new" inline) and the existing profile classes only.

export interface EmployeePayRatesPanelProps {
  employeeId: string;
  onChanged?: (() => void) | undefined;
}

// The six pay bases are offered for a new rate; the legacy Commission, Tips and Executive bonus stay in history and keep their labels in the table.
const PAY_TYPES = BASE_PAY_BASES.map((basis) => ({ value: payTypeFromPayBasis(basis), label: payBasisLabels[basis] }));
const BASES = Object.entries(workRateAmountBasisLabels).map(([value, label]) => ({ value: Number(value), label }));

/** A sensible default basis for a pay type; the user can change it. Hourly -> per hour, Salary -> annual. */
function defaultBasis(payType: number): number {
  return payType === 1 ? 1 : payType === 2 ? 3 : 2;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** Whole dollars and at most two decimals — the column is decimal(18,2). Returns the number or null. */
function parseRate(raw: string): number | null {
  const t = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function message(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function EmployeePayRatesPanel({ employeeId, onChanged }: EmployeePayRatesPanelProps) {
  // The reimbursement sentence is about Medicare and payers, so it appears only for a healthcare company (universal compensation).
  const { isHealthcare } = useCompanyIndustry();
  const uid = useId();
  const [assignments, setAssignments] = useState<WorkRateAssignmentResponse[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<WorkRateResponse[]>([]);
  const [templatesNote, setTemplatesNote] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [label, setLabel] = useState('');
  const [payType, setPayType] = useState('1');
  const [rate, setRate] = useState('');
  const [startDate, setStartDate] = useState(todayIso());
  const [isPrimary, setIsPrimary] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const [addingTemplate, setAddingTemplate] = useState(false);
  const [tName, setTName] = useState('');
  const [tBasis, setTBasis] = useState('1');
  const [tError, setTError] = useState<string | null>(null);
  const [tBusy, setTBusy] = useState(false);

  const [endingId, setEndingId] = useState<string | null>(null);
  const [endDate, setEndDate] = useState(todayIso());
  const [endError, setEndError] = useState<string | null>(null);
  const [endBusy, setEndBusy] = useState(false);

  const [reloadNonce, setReloadNonce] = useState(0);

  /** Re-reads the list from the server; the effect below does the read (setState only in its callbacks). */
  const loadAssignments = useCallback(async () => {
    setLoadError(null);
    setReloadNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    compensationGateway
      .listWorkRateAssignments(employeeId, controller.signal)
      .then((list) => { if (!controller.signal.aborted) setAssignments(list); })
      .catch((err: unknown) => { if (!controller.signal.aborted) setLoadError(message(err, 'Could not load pay rates.')); });
    return () => controller.abort();
  }, [employeeId, reloadNonce]);

  useEffect(() => {
    const controller = new AbortController();
    payConfigGateway
      .listWorkRates(controller.signal)
      .then((list) => { if (!controller.signal.aborted) setTemplates(list.filter((t) => t.isActive)); })
      .catch(() => {
        // Templates are optional: without them (e.g. no work-rates permission) a custom rate still works.
        if (!controller.signal.aborted) setTemplatesNote('Rate templates could not be loaded. You can still enter a custom rate.');
      });
    return () => controller.abort();
  }, []);

  function resetForm() {
    setTemplateId('');
    setLabel('');
    setPayType('1');
    setRate('');
    setStartDate(todayIso());
    setIsPrimary(true);
    setErrors({});
    setSubmitError(null);
    setAddingTemplate(false);
  }

  function pickTemplate(id: string) {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) {
      setLabel(t.label);
      setPayType(String(t.payType));
      setRate(String(t.amount));
    }
  }

  async function createTemplate() {
    const name = tName.trim();
    const amount = parseRate(rate);
    const problems: string[] = [];
    if (!name) problems.push('Enter a name for the rate template.');
    if (amount === null || amount <= 0) problems.push('Enter the pay rate amount above first (greater than zero, up to 2 decimals).');
    if (problems.length > 0) {
      setTError(problems.join(' '));
      return;
    }
    setTError(null);
    setTBusy(true);
    try {
      const created = await payConfigGateway.createWorkRate({
        jobTitleId: null, label: name, payType: Number(payType), amount: amount!, amountBasis: Number(tBasis),
      });
      setTemplates((prev) => (prev.some((t) => t.id === created.id) ? prev : [...prev, created]));
      setTemplateId(created.id);
      setLabel(created.label);
      setAddingTemplate(false);
      setTName('');
    } catch (err) {
      setTError(message(err, 'Could not add the rate template. You may not have permission to add rate templates.'));
    } finally {
      setTBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!label.trim()) next.label = 'Enter a label for this pay rate.';
    if (!payTypeOptions.some((p) => p.value === Number(payType))) next.payType = 'Choose a pay type.';
    const amount = parseRate(rate);
    if (amount === null) next.rate = 'Enter the rate as a number with up to 2 decimals.';
    else if (amount <= 0) next.rate = 'Pay rate must be greater than zero.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) next.startDate = 'Enter an effective start date.';
    setErrors(next);
    setSubmitError(null);
    setStatus(null);
    if (Object.keys(next).length > 0) return;

    setSaving(true);
    try {
      await compensationGateway.createWorkRateAssignment(employeeId, {
        workRateId: templateId || null,
        label: label.trim(),
        payType: Number(payType),
        rate: amount!,
        isPrimary,
        effectiveStartDate: startDate,
      });
      await loadAssignments();
      setShowForm(false);
      resetForm();
      setStatus('Pay rate saved.');
      onChanged?.();
    } catch (err) {
      setSubmitError(message(err, 'Could not save the pay rate. You may not have permission to change pay rates.'));
    } finally {
      setSaving(false);
    }
  }

  async function endRate(a: WorkRateAssignmentResponse) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
      setEndError('Enter an end date.');
      return;
    }
    if (endDate <= a.effectiveStartDate) {
      setEndError('The end date must be after the start date.');
      return;
    }
    setEndError(null);
    setEndBusy(true);
    setStatus(null);
    try {
      await compensationGateway.endWorkRateAssignment(a.id, endDate);
      await loadAssignments();
      setEndingId(null);
      setStatus('Pay rate ended.');
      onChanged?.();
    } catch (err) {
      setEndError(message(err, 'Could not end the pay rate. You may not have permission to change pay rates.'));
    } finally {
      setEndBusy(false);
    }
  }

  const fieldId = (name: string) => `${uid}-${name}`;
  const rateBasis = payBasisFromPayType(Number(payType));
  // Commission, Tips and Executive bonus are not offered for a new rate. One is listed only when the form already carries it (a rate template that has it),
  // so the choice on screen is always the value that will be saved.
  const legacyChosen = [3, 4, 5].includes(Number(payType));
  const payTypeOptions = legacyChosen ? [...PAY_TYPES, { value: Number(payType), label: `${compensationPayTypeLabels[Number(payType)] ?? 'Existing pay type'} (existing)` }] : PAY_TYPES;
  const err = (name: string) => (errors[name] ? { 'aria-invalid': true, 'aria-describedby': fieldId(`${name}-err`) } : {});
  const fieldError = (name: string) =>
    errors[name] ? <span id={fieldId(`${name}-err`)} className="error" role="alert">{errors[name]}</span> : null;

  return (
    <div aria-label="Pay rates">
      {status && <p className="muted" role="status">{status}</p>}

      {loadError ? (
        <div role="alert">
          <p className="error">{loadError}</p>
          <button type="button" onClick={() => void loadAssignments()}>Retry</button>
        </div>
      ) : assignments === null ? (
        <p className="muted" role="status">Loading pay rates…</p>
      ) : (
        <table aria-label="Pay rates for this employee">
          <thead>
            <tr><th>Label</th><th>Pay type</th><th>Rate</th><th>Effective</th><th>Primary</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {assignments.length === 0 && (
              <tr><td colSpan={6} className="muted">No pay rates yet. Add a primary pay rate so this employee can be paid.</td></tr>
            )}
            {assignments.map((a) => (
              <tr key={a.id}>
                <td>{a.label}</td>
                <td>{compensationPayTypeLabels[a.payType] ?? 'Unknown'}</td>
                <td>{money(a.rate)}</td>
                <td>{a.effectiveStartDate} – {a.effectiveEndDate ?? 'ongoing'}</td>
                <td>{a.isPrimary ? 'Yes' : 'No'}</td>
                <td>
                  {a.effectiveEndDate === null && endingId !== a.id && (
                    <button type="button" className="link-button" aria-label={`End pay rate ${a.label}`}
                      onClick={() => { setEndingId(a.id); setEndDate(todayIso()); setEndError(null); }}>
                      End rate
                    </button>
                  )}
                  {endingId === a.id && (
                    <div role="group" aria-label={`End pay rate ${a.label}`}>
                      <label>
                        End date
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                          aria-invalid={endError ? true : undefined} aria-describedby={endError ? fieldId('end-err') : undefined} />
                      </label>
                      <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                        <button type="button" disabled={endBusy} onClick={() => void endRate(a)}>
                          {endBusy ? 'Ending…' : 'Confirm end'}
                        </button>
                        <button type="button" disabled={endBusy} onClick={() => setEndingId(null)}>Cancel</button>
                      </div>
                      {endError && <p id={fieldId('end-err')} className="error" role="alert">{endError}</p>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!showForm ? (
        <div style={{ marginTop: 12 }}>
          <button type="button" onClick={() => { resetForm(); setShowForm(true); }}>Add pay rate</button>
        </div>
      ) : (
        <form onSubmit={(e) => void submit(e)} noValidate style={{ marginTop: 12 }} aria-label="Add pay rate">
          <div className="profile-field-grid">
            <div>
              <label>
                Rate template
                <select value={templateId} onChange={(e) => pickTemplate(e.target.value)}>
                  <option value="">Custom rate (no template)</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label} — {money(t.amount)} ({workRateAmountBasisLabels[t.amountBasis] ?? ''})
                    </option>
                  ))}
                </select>
              </label>
              {templatesNote && <p className="muted">{templatesNote}</p>}
              {addingTemplate ? (
                <div role="group" aria-label="Add a new rate template" style={{ marginTop: 6 }}>
                  <label>
                    New rate template name
                    <input value={tName} maxLength={100} onChange={(e) => setTName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void createTemplate(); } else if (e.key === 'Escape') setAddingTemplate(false); }} />
                  </label>
                  <label>
                    Template amount basis
                    <select value={tBasis} onChange={(e) => setTBasis(e.target.value)}>
                      {BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                    </select>
                  </label>
                  <p className="muted">The template uses the pay type and rate entered below.</p>
                  <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
                    <button type="button" disabled={tBusy} onClick={() => void createTemplate()}>
                      {tBusy ? 'Adding…' : 'Add rate template'}
                    </button>
                    <button type="button" disabled={tBusy} onClick={() => { setAddingTemplate(false); setTError(null); }}>Cancel</button>
                  </div>
                  {tError && <p className="error" role="alert">{tError}</p>}
                </div>
              ) : (
                <button type="button" className="link-button" style={{ marginTop: 4 }}
                  onClick={() => { setAddingTemplate(true); setTBasis(String(defaultBasis(Number(payType)))); setTError(null); }}>
                  + Add new rate template
                </button>
              )}
            </div>
            <div>
              <label>
                Label
                <input value={label} maxLength={100} onChange={(e) => setLabel(e.target.value)} {...err('label')} />
              </label>
              {fieldError('label')}
            </div>
            <div>
              <label>
                Pay type
                <select value={payType} onChange={(e) => setPayType(e.target.value)} {...err('payType')}>
                  {payTypeOptions.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </label>
              {fieldError('payType')}
            </div>
            <div>
              <label>
                Pay rate (USD)
                <input inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} aria-describedby={`${fieldId('rate-help')}${errors.rate ? ` ${fieldId('rate-err')}` : ''}`} aria-invalid={errors.rate ? true : undefined} />
              </label>
              {rateBasis && (
                <p id={fieldId('rate-help')} className="muted" data-testid="rate-basis-help">
                  <strong>{rateLabels[rateBasis]}.</strong> {rateBasisHelp[rateBasis]}{isHealthcare ? ` ${REIMBURSEMENT_SEPARATE_SHORT}` : ''}
                </p>
              )}
              {!rateBasis && <span id={fieldId('rate-help')} className="muted">{isHealthcare ? REIMBURSEMENT_SEPARATE_SHORT : 'What your company pays for one unit of this pay type.'}</span>}
              {fieldError('rate')}
            </div>
            <div>
              <label>
                Effective start date
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} {...err('startDate')} />
              </label>
              {fieldError('startDate')}
            </div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
              Primary pay rate
            </label>
          </div>
          {isPrimary && (
            <p className="muted">Saving a primary rate ends this employee&apos;s current primary rate on the start date.</p>
          )}
          {submitError && <p className="error" role="alert">{submitError}</p>}
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save pay rate'}</button>
            <button type="button" disabled={saving} onClick={() => { setShowForm(false); resetForm(); }}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
